import React from "react";
import PropTypes from "prop-types";
import { useFetch } from "./authentication";

const PermissionsContext = React.createContext(null);

export const usePermissions = () => {
	return React.useContext(PermissionsContext);
};

export const PermissionsLayer = ({ children }) => {
	const [currentPermissions, setCurrentPermissions] = React.useState(null);
	const fetch = useFetch();

	// Get permissions
	React.useEffect(() => {
		async function loadRights() {
			// Mithrandir links have trailing slash
			const response = await fetch(
				`${window.weezConfig.api.apiUrl}/auth/_self/`
			);
			const { roles } = await response.json();
			return new Permissions(roles);
		}

		loadRights().then(permissions => setCurrentPermissions(permissions));
	}, [fetch]);

	if (!currentPermissions) {
		return null;
	}

	return (
		<PermissionsContext.Provider value={currentPermissions}>
			{children}
		</PermissionsContext.Provider>
	);
};

PermissionsLayer.propTypes = {
	children: PropTypes.node
};

class Permissions {
	constructor(roles) {
		this.roles = roles || [];
	}

	hasRole(role) {
		return Boolean(
			this.roles.find(r => r.role.slug === role.toLowerCase())
		);
	}

	isAdmin() {
		return Boolean(
			this.roles.find(
				r => r.role.slug === "admin" && r.context.level === 0
			)
		);
	}

	hasCapacity(capacity, organization_id) {
		return Boolean(
			this.roles
				// lookup match roles
				.filter(
					r =>
						r.context.level === 0 ||
						(organization_id &&
							r.context.level === 3 &&
							r.context.value === organization_id.toString())
				)
				// find at least one role with the capacity
				.find(r => r.role.capacities.find(c => c.slug === capacity))
		);
	}
	capacityOrga(organization_id) {
		const capacities = [];
		this.roles
			.filter(
				r =>
					r.context.level === 0 ||
					(r.context.level === 3 &&
						r.context.value === organization_id.toString())
			)
			.map(role =>
				role.role.capacities.forEach(capacity =>
					capacities.push(capacity.slug)
				)
			);

		return new Set(capacities);
	}

	get capacities() {
		const capacities = [];
		this.roles.map(role =>
			role.role.capacities.forEach(capacity =>
				capacities.push(capacity.slug)
			)
		);
		return new Set(capacities);
	}

	hasRightsOnOrga(organization_id) {
		return Boolean(
			this.roles.find(r => {
				return (
					r.role.slug !== "minisite" &&
					r.context.level === 3 &&
					r.context.value === organization_id.toString()
				);
			})
		);
	}

	hasPermissionsCrossOrga() {
		//TODO : Remove filter on slug when nBO is open
		return Boolean(
			this.roles.find(
				r => r.context.level === 0 && r.role.slug !== "minisite"
			)
		);
	}

	canAccessNBO(organization_id) {
		const isAdmin = this.isAdmin();
		const hasPermissionsCrossOrga = this.hasPermissionsCrossOrga();
		const hasRightsOnOrga = this.hasRightsOnOrga(organization_id);

		return isAdmin || hasPermissionsCrossOrga || hasRightsOnOrga;
	}

	hasCapacities(capacities, organizationId) {
		const userCapacities = this.capacityOrga(organizationId);
		return Boolean(
			capacities.find(capacity => userCapacities.has(capacity))
		);
	}
}
